// CheckRow: a checklist line you tap to mark done, for packing and shop-floor lists. The whole
// row is the target; a hold (when the list needs one) sets the item aside, with a ring that
// fills around the box while the finger is down. Marking an item done is this surface's one
// moment: the box lands on the bouncy spring and flares. docs/operations/design-standard.md,
// "Controls".
//
// Only the label side is role=checkbox. Fields in `trailing` sit beside it, not inside it,
// because a checkbox's children are presentational and a field inside one is hidden from a
// screen reader.

import React, { useEffect, useRef, useState } from 'react';
import Appear from './Appear';
import Reveal from './Reveal';
import { CheckMark, CheckState, CheckTone } from './Checkbox';
import { CONTROL_MOTION, EASE, FOCUS, MS, STATE, reducedMotion } from './motion';

let seq = 0;

const RING = 2 * Math.PI * 14;
/** A drag this far is a scroll or a slip, not a tap. */
const SLOP_PX = 10;

const HALO: Record<CheckTone, string> = {
  primary: 'shadow-glow-soft',
  good: 'shadow-[0_0_18px_0_rgb(34_197_94/0.4)]',
};

export interface CheckRowProps {
  /** `mixed` is an item set aside: struck through, with `mixedWord` beside it. */
  checked: CheckState;
  /** Tap, click, Space or Enter. The list decides what that does in each state. */
  onToggle: () => void;
  /** Holding the row (or holding Space or Enter). Omit where a hold means nothing. */
  onHold?: () => void;
  /** Says what a hold does, for a screen reader ("Hold to skip"). Keep a visible legend on the page too. */
  holdHint?: string;
  /** False keeps the hold to the pointer: Space and Enter then toggle on key-down, never skip. */
  holdKeyboard?: boolean;
  holdMs?: number;
  label: React.ReactNode;
  /** The word for the mixed state, shown beside the label. */
  mixedWord?: string;
  /** A line under the label once the item is done or set aside: who, when, how many. */
  meta?: React.ReactNode;
  /** Fields beside the label (a quantity, a note). Their keys and taps never toggle the row. */
  trailing?: React.ReactNode;
  /** `good` where done is the meaning: the box goes green. */
  tone?: CheckTone;
  /** A save is in flight: input is ignored until it lands. */
  busy?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export const CheckRow: React.FC<CheckRowProps> = ({
  checked,
  onToggle,
  onHold,
  holdHint,
  holdKeyboard = true,
  holdMs = 500,
  label,
  mixedWord = 'Skipped',
  meta,
  trailing,
  tone = 'primary',
  busy = false,
  disabled = false,
  className = '',
  style,
}) => {
  const [ids] = useState(() => {
    seq += 1;
    return { label: `ui-checkrow-${seq}-label`, word: `ui-checkrow-${seq}-word`, meta: `ui-checkrow-${seq}-meta`, hint: `ui-checkrow-${seq}-hint` };
  });
  const [holding, setHolding] = useState(false);
  const [landings, setLandings] = useState(0);
  const timer = useRef<number | undefined>(undefined);
  /** The hold fired, so the click or key-up that follows is not a tap. */
  const held = useRef(false);
  const moved = useRef(false);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const keyHeld = useRef(false);
  const box = useRef<HTMLSpanElement>(null);
  // The timer fires the latest handlers, not the ones captured when the press began.
  const latest = useRef({ onToggle, onHold });
  latest.current = { onToggle, onHold };

  const blocked = disabled || busy;
  const canHold = !!onHold && !blocked;
  const mixed = checked === 'mixed';

  const cancel = () => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    setHolding(false);
  };

  const begin = () => {
    held.current = false;
    if (!canHold) return;
    window.clearTimeout(timer.current);
    setHolding(true);
    timer.current = window.setTimeout(() => {
      timer.current = undefined;
      held.current = true;
      setHolding(false);
      latest.current.onHold?.();
    }, holdMs);
  };

  useEffect(() => {
    if (canHold) return;
    window.clearTimeout(timer.current);
    timer.current = undefined;
    setHolding(false);
  }, [canHold]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // The landing: only on a change to done after mount, never for rows that load already done.
  const was = useRef(checked);
  useEffect(() => {
    const before = was.current;
    was.current = checked;
    if (checked !== true || before === true) return;
    setLandings((n) => n + 1);
    const el = box.current;
    if (!el || reducedMotion() || typeof el.animate !== 'function') return;
    // animate() throws on an easing it cannot parse (linear() before Safari 17.2), where CSS would only ignore it.
    try {
      el.animate([{ transform: 'scale(0.75)' }, { transform: 'scale(1)' }], { duration: MS.spring, easing: EASE.springBouncy });
    } catch {
      el.animate([{ transform: 'scale(0.75)' }, { transform: 'scale(1)' }], { duration: MS.spring, easing: EASE.emphasized });
    }
  }, [checked]);

  const toggle = () => {
    if (!blocked) latest.current.onToggle();
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button > 0) return;
    origin.current = { x: e.clientX, y: e.clientY };
    moved.current = false;
    begin();
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const o = origin.current;
    if (!o || moved.current) return;
    if (Math.abs(e.clientX - o.x) > SLOP_PX || Math.abs(e.clientY - o.y) > SLOP_PX) {
      moved.current = true;
      cancel();
    }
  };
  const release = () => {
    origin.current = null;
    if (timer.current !== undefined) cancel();
  };
  const onClick = () => {
    if (held.current) {
      held.current = false;
      return;
    }
    if (moved.current) {
      moved.current = false;
      return;
    }
    toggle();
  };

  const isKey = (e: React.KeyboardEvent) => e.key === ' ' || e.key === 'Enter';
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget || !isKey(e)) return;
    e.preventDefault();
    if (e.repeat || blocked) return;
    if (canHold && holdKeyboard) {
      keyHeld.current = true;
      begin();
    } else {
      toggle();
    }
  };
  const onKeyUp = (e: React.KeyboardEvent) => {
    if (e.target !== e.currentTarget || !isKey(e) || !keyHeld.current) return;
    keyHeld.current = false;
    if (held.current) {
      held.current = false;
      return;
    }
    cancel();
    toggle();
  };

  const onBlur = () => {
    keyHeld.current = false;
    if (timer.current !== undefined) cancel();
  };

  const describedBy = [meta != null && checked !== false ? ids.meta : null, canHold && holdHint ? ids.hint : null].filter(Boolean).join(' ');

  return (
    <div className={`flex min-w-0 items-stretch rounded-control ${className}`} style={style}>
      <div
        role="checkbox"
        tabIndex={disabled ? -1 : 0}
        aria-checked={mixed ? 'mixed' : checked}
        aria-disabled={disabled || undefined}
        aria-busy={busy || undefined}
        aria-labelledby={mixed ? `${ids.label} ${ids.word}` : ids.label}
        aria-describedby={describedBy || undefined}
        onClick={onClick}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onBlur={onBlur}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={release}
        onPointerLeave={release}
        onPointerCancel={release}
        onContextMenu={onHold ? (e) => e.preventDefault() : undefined}
        className={`group/mark relative flex min-h-14 min-w-0 flex-1 select-none items-center gap-3 rounded-control px-3 py-2.5 text-left [-webkit-touch-callout:none] ${CONTROL_MOTION} ${FOCUS} ${
          disabled ? 'cursor-not-allowed' : busy ? `cursor-progress ${STATE}` : `cursor-pointer ${STATE}`
        }`}
      >
        <span className="relative inline-flex flex-none">
          <CheckMark ref={box} state={checked} tone={tone} mixedTone="warn" size="lg" disabled={disabled} />
          {landings > 0 && checked === true && (
            <span key={landings} aria-hidden="true" className={`pointer-events-none absolute inset-0 rounded-control opacity-0 animate-flare ${HALO[tone]}`} />
          )}
          {onHold && (
            <svg
              aria-hidden="true"
              focusable="false"
              viewBox="0 0 32 32"
              fill="none"
              className={`pointer-events-none absolute -left-1 -top-1 h-8 w-8 -rotate-90 text-amber-400 transition-opacity ease-emphasized ${
                holding ? 'opacity-100 duration-quick' : 'opacity-0 duration-exit'
              }`}
            >
              <circle cx="16" cy="16" r="14" stroke="currentColor" strokeOpacity={0.2} strokeWidth={2} />
              {/* Linear on purpose: the ring is the hold's clock, and an eased clock lies about when it ends. */}
              <circle
                cx="16"
                cy="16"
                r="14"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeDasharray={RING}
                style={{
                  strokeDashoffset: holding ? 0 : RING,
                  transition: holding ? `stroke-dashoffset ${holdMs}ms linear` : `stroke-dashoffset ${MS.exit}ms ${EASE.exit}`,
                }}
              />
            </svg>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
            <span
              id={ids.label}
              className={`min-w-0 text-sm font-medium leading-5 decoration-on-surface-variant transition-[color] duration-standard ease-emphasized ${
                disabled ? 'text-on-surface-disabled' : mixed ? 'text-on-surface-variant line-through' : 'text-on-surface'
              }`}
            >
              {label}
            </span>
            <Appear show={mixed} className="inline-flex flex-none">
              <span id={ids.word} className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium leading-4 text-amber-300">
                {mixedWord}
              </span>
            </Appear>
          </div>
          <Reveal show={meta != null && checked !== false}>
            <span id={ids.meta} className="mt-0.5 flex min-w-0 items-center gap-2 text-xs leading-4 text-on-surface-variant">
              {meta}
            </span>
          </Reveal>
        </div>
        {canHold && holdHint && (
          <span id={ids.hint} className="sr-only">
            {holdHint}
          </span>
        )}
      </div>
      <Appear show={trailing != null} as="div" className="flex flex-none items-center pr-3">
        {trailing}
      </Appear>
    </div>
  );
};

export default CheckRow;
